/**
 * Xero invoice "Status & Amounts" fieldset extracted from XeroManualForm
 * to keep the parent component under the max-lines ceiling.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { XeroInvoiceStatus } from '../../types'
import { MoneyField, SelectField } from './xero-form-status-amounts/xero-form-status-amounts.parts'

const CURRENCIES: readonly string[] = ['EUR', 'AUD', 'CHF', 'GBP', 'NZD', 'USD']
const CURRENCY_OPTIONS = CURRENCIES.map((currency) => ({ value: currency, label: currency }))

interface XeroFormStatusAmountsProps {
  formData: {
    invoiceStatus: XeroInvoiceStatus
    currencyCode: string
    amountDue: string
    amountPaid: string
  }
  invoiceStatuses: { value: XeroInvoiceStatus; label: string }[]
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
}

export function XeroFormStatusAmounts({
  formData,
  invoiceStatuses,
  onChange,
}: XeroFormStatusAmountsProps): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <fieldset className="space-y-4">
      <legend className="text-sm font-medium text-stone-700 dark:text-stone-300">
        {t('licensing.xero.statusAndAmounts')}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          id="invoiceStatus"
          name="invoiceStatus"
          value={formData.invoiceStatus}
          label={t('licensing.status')}
          required
          options={invoiceStatuses}
          onChange={onChange}
        />
        <SelectField
          id="currencyCode"
          name="currencyCode"
          value={formData.currencyCode}
          label={t('licensing.xero.currency')}
          options={CURRENCY_OPTIONS}
          onChange={onChange}
        />
        <MoneyField
          id="amountDue"
          name="amountDue"
          value={formData.amountDue}
          label={t('licensing.xero.amountDue')}
          onChange={onChange}
        />
        <MoneyField
          id="amountPaid"
          name="amountPaid"
          value={formData.amountPaid}
          label={t('licensing.xero.amountPaid')}
          onChange={onChange}
        />
      </div>
    </fieldset>
  )
}
